import React, { useState } from 'react';
import { ToggleSwitch } from '../../../../components/ToggleSwitch';
import { FileBrowser } from '../../../../components/storage';
import { useStorageListing } from '../hooks/useStorageListing';

interface FtpConfig {
  hostname: string;
  port: number;
  login: string;
  password: string;
  secure: boolean;
}

interface Props {
  config: FtpConfig;
  onChange: (config: FtpConfig) => void;
  selectedPath: string | null;
  onPathChange: (path: string) => void;
  disabled: boolean;
}

export function FtpSource({ config, onChange, selectedPath, onPathChange, disabled }: Props) {
  const [baseDir, setBaseDir] = useState('');
  // The listing only runs once the user clicks Connect (or navigates a directory).
  const [connected, setConnected] = useState(false);

  const provider = config.secure ? 'sftp' : 'ftp';
  const { data, isFetching, refetch } = useStorageListing(
    provider,
    { hostname: config.hostname, port: config.port, login: config.login, password: config.password, baseDir },
    connected,
  );

  const set = (key: keyof FtpConfig, value: any) => onChange({ ...config, [key]: value });

  const connect = () => {
    setConnected(true);
    refetch();
  };

  const navigate = (path: string) => {
    setBaseDir(path);
    refetch();
  };

  const levelUp = () => {
    const parts = baseDir.split('/').filter(Boolean);
    parts.pop();
    navigate(parts.join('/'));
  };

  // Toggling secure flips the default port the same way the Angular watch did.
  const toggleSecure = (secure: boolean) => onChange({ ...config, secure, port: secure ? 22 : 21 });

  return (
    <div>
      <form onSubmit={e => { e.preventDefault(); connect(); }} autoComplete="off" noValidate>
        <h4>
          <div className="row">
            <div className="col-md-6">
              <button type="submit" className="btn btn-primary btn-xs" disabled={isFetching || disabled} data-testid="add-collection-ftp-connect-btn">
                <i className={`fas ${isFetching ? 'fa-spinner fa-spin' : 'fa-bolt'}`} aria-hidden="true" /> Connect
              </button>
            </div>
            <div className="col-md-6 text-right">
              <ToggleSwitch
                checked={config.secure}
                onChange={toggleSecure}
                disabled={disabled}
                onLabel="SFTP"
                testId="add-collection-ftp-secure-toggle"
                offLabel="FTP"
              />
            </div>
          </div>
        </h4>

        <div className="form-group">
          <div className="row">
            <div className="col-md-4">
              <input
                type="text"
                placeholder="Host"
                data-testid="add-collection-ftp-host-input"
                className="form-control"
                value={config.hostname}
                onChange={e => set('hostname', e.target.value)}
                maxLength={255}
                disabled={disabled}
              />
            </div>
            <div className="col-md-2">
              <input
                type="number"
                placeholder="Port"
                data-testid="add-collection-ftp-port-input"
                className="form-control"
                value={config.port}
                onChange={e => set('port', parseInt(e.target.value, 10) || (config.secure ? 22 : 21))}
                disabled={disabled}
              />
            </div>
            <div className="col-md-3">
              <input
                type="text"
                placeholder="Login"
                data-testid="add-collection-ftp-login-input"
                className="form-control"
                value={config.login}
                onChange={e => set('login', e.target.value)}
                maxLength={255}
                disabled={disabled}
              />
            </div>
            <div className="col-md-3">
              <input
                type="password"
                autoComplete="new-password"
                placeholder="Password"
                data-testid="add-collection-ftp-password-input"
                className="form-control"
                value={config.password}
                onChange={e => set('password', e.target.value)}
                maxLength={255}
                disabled={disabled}
              />
            </div>
          </div>
        </div>
      </form>

      <FileBrowser
        pathLabel={baseDir}
        files={data ?? []}
        loading={isFetching}
        selectedPath={selectedPath}
        onSelect={onPathChange}
        onNavigate={navigate}
        onLevelUp={levelUp}
        testId="add-collection-ftp-browser-panel"
      />
    </div>
  );
}

export function defaultFtpConfig(): FtpConfig {
  return { hostname: '', port: 21, login: '', password: '', secure: false };
}
